import DOMPurify from "dompurify";

// Client-side mirror of the backend bluemonday allowlist
// (internal/pseudonymizer/core/sanitize.go). The backend is the security source
// of truth — it re-sanitizes every broadcast before storing — so this config is
// defense-in-depth for rendering and a safety net before we POST. Keep the two
// in sync if the allowed markup ever changes.
const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "s",
    "h1",
    "h2",
    "h3",
    "ol",
    "ul",
    "li",
    "a",
    "span",
  ],
  ALLOWED_ATTR: ["href", "style", "target", "rel"],
};

/** Sanitize rich notification HTML for safe rendering / sending. */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, SANITIZE_CONFIG) as unknown as string;
}

/**
 * Strip rich HTML down to a single-line plain-text excerpt for compact previews
 * (notification list / dropdown). Block boundaries become spaces so words don't
 * run together.
 */
export function htmlToText(html: string): string {
  const clean = sanitizeHtml(html)
    .replace(/<\/(p|h[1-3]|li|ul|ol)>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ");
  const doc = new DOMParser().parseFromString(clean, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

/** True when there is no visible text or content once markup is stripped. */
export function isHtmlEmpty(html: string): boolean {
  return htmlToText(html).length === 0;
}
